/**
 * The emoji the composer offers, and the few this device reached for last.
 *
 * A hand-picked list rather than a dataset dependency: a picker only needs the
 * emoji people actually send, and keeping the catalogue here means no extra
 * megabyte in the bundle, no image sprites, and one place to add to. Every
 * entry is written as the character followed by the words it should be found
 * by, so the data stays readable as data.
 */

export type Emoji = { char: string; keywords: string }

export type EmojiGroup = { name: string; emoji: Emoji[] }

/**
 * The reactions offered first — on the hover bar, in the long-press menu and
 * at the top of the full picker. The full catalogue is one tap further.
 */
export const QUICK_REACTIONS = [
  "👍",
  "❤️",
  "😂",
  "😮",
  "😢",
  "🙏",
  "🔥",
] as const

/** What a double tap on a message puts on it. */
export const DOUBLE_TAP_REACTION = "❤️"

const CATALOGUE: Record<string, string[]> = {
  Смайлики: [
    "😀 улыбка радость",
    "😃 улыбка радость",
    "😄 смех радость",
    "😁 улыбка зубы",
    "😆 смех хохот",
    "😅 смех пот неловко",
    "🤣 ржу смех катаюсь",
    "😂 слёзы смех плачу",
    "🙂 лёгкая улыбка",
    "🙃 вверх ногами ирония",
    "😉 подмигиваю",
    "😊 улыбка румянец",
    "😇 ангел нимб",
    "🥰 влюблён сердечки",
    "😍 влюблён глаза",
    "🤩 восторг звёзды",
    "😘 поцелуй",
    "😗 поцелуй",
    "😎 крутой очки",
    "🤓 ботаник очки",
    "🧐 монокль",
    "🤔 думаю размышляю",
    "🤨 бровь недоверие",
    "😐 нейтрально",
    "😑 без эмоций",
    "😶 без рта молчу",
    "🙄 закатываю глаза",
    "😏 ухмылка",
    "😒 недовольство",
    "😞 разочарование",
    "😔 грусть печаль",
    "😟 беспокойство",
    "😕 замешательство",
    "🙁 грусть",
    "😣 упорство",
    "😖 растерянность",
    "😫 устал",
    "😩 измучен",
    "🥺 умоляю глазки",
    "😢 плачу слеза",
    "😭 рыдаю плачу",
    "😤 злюсь пар",
    "😠 злость",
    "😡 ярость злость",
    "🤯 взрыв мозга",
    "😳 смущение шок",
    "🥵 жара жарко",
    "🥶 холод мёрзну",
    "😱 ужас крик",
    "😨 страх",
    "😰 тревога пот",
    "😴 сплю",
    "🤤 слюни",
    "😪 сонный",
    "😵 голова кружится",
    "🤐 рот на замке",
    "🤢 тошнит",
    "🤮 рвота",
    "🤧 чихаю",
    "😷 маска болезнь",
    "🤒 температура болею",
    "🤕 травма бинт",
    "🥳 праздник вечеринка",
    "🤗 обнимаю",
    "🤭 хихикаю ой",
    "🤫 тише секрет",
    "🤥 вру нос",
    "😬 неловко зубы",
    "🙈 не вижу обезьяна",
    "🙉 не слышу обезьяна",
    "🙊 не скажу обезьяна",
    "💀 череп смерть",
    "👻 призрак привидение",
    "🤖 робот бот",
    "👽 инопланетянин",
    "🤡 клоун",
    "💩 какашка",
  ],
  Жесты: [
    "👍 палец вверх лайк класс",
    "👎 палец вниз дизлайк",
    "👌 окей отлично",
    "🤌 щепотка итальянец",
    "✌️ мир виктория",
    "🤞 скрещенные пальцы удача",
    "🤟 люблю тебя",
    "🤘 рок коза",
    "🤙 позвони",
    "👈 палец влево",
    "👉 палец вправо",
    "👆 палец вверх",
    "👇 палец вниз",
    "✋ ладонь стоп",
    "🖐️ ладонь",
    "🖖 вулкан спок",
    "👋 привет пока машу",
    "🤝 рукопожатие договорились",
    "🙏 спасибо пожалуйста молю",
    "✍️ пишу",
    "💪 сила бицепс",
    "👏 аплодисменты браво",
    "🙌 ура руки вверх",
    "🤲 ладони",
    "🫶 сердце руками",
    "🤦 фейспалм",
    "🤷 пожимаю плечами не знаю",
    "🙋 руку поднял",
    "💅 маникюр пофиг",
    "👀 глаза смотрю",
  ],
  Символы: [
    "❤️ сердце любовь",
    "🧡 сердце оранжевое",
    "💛 сердце жёлтое",
    "💚 сердце зелёное",
    "💙 сердце синее",
    "💜 сердце фиолетовое",
    "🖤 сердце чёрное",
    "🤍 сердце белое",
    "💔 разбитое сердце",
    "💕 два сердца",
    "💖 сверкающее сердце",
    "💘 стрела сердце",
    "💯 сто балов точно",
    "💥 взрыв бум",
    "💫 звёзды головокружение",
    "⭐ звезда",
    "🌟 звезда сияние",
    "✨ искры блеск",
    "🔥 огонь круто",
    "⚡ молния",
    "❄️ снежинка холод",
    "✅ галочка готово",
    "❌ крест нет",
    "⚠️ внимание предупреждение",
    "❓ вопрос",
    "❗ восклицание",
    "🎉 праздник конфетти",
    "🎊 конфетти",
    "🎁 подарок",
    "🏆 кубок победа",
    "🥇 первое место золото",
    "💰 деньги мешок",
    "💸 деньги улетают",
    "🔔 звонок уведомление",
    "🔕 без звука",
    "🔒 замок закрыто",
    "🔓 замок открыто",
    "♻️ переработка",
    "🆗 окей",
    "🆘 помогите sos",
  ],
  Животные: [
    "🐶 собака пёс",
    "🐱 кошка кот",
    "🐭 мышь",
    "🐹 хомяк",
    "🐰 кролик заяц",
    "🦊 лиса",
    "🐻 медведь",
    "🐼 панда",
    "🐨 коала",
    "🐯 тигр",
    "🦁 лев",
    "🐮 корова",
    "🐷 свинья",
    "🐸 лягушка",
    "🐵 обезьяна",
    "🐔 курица",
    "🐧 пингвин",
    "🐦 птица",
    "🦆 утка",
    "🦅 орёл",
    "🦉 сова",
    "🐺 волк",
    "🐝 пчела",
    "🐛 гусеница жук",
    "🦋 бабочка",
    "🐌 улитка",
    "🐢 черепаха",
    "🐍 змея",
    "🐙 осьминог",
    "🦀 краб",
    "🐬 дельфин",
    "🐳 кит",
    "🦈 акула",
    "🐟 рыба",
    "🦄 единорог",
    "🐴 лошадь",
  ],
  Еда: [
    "🍏 яблоко зелёное",
    "🍎 яблоко",
    "🍐 груша",
    "🍊 апельсин мандарин",
    "🍋 лимон",
    "🍌 банан",
    "🍉 арбуз",
    "🍇 виноград",
    "🍓 клубника",
    "🫐 черника",
    "🍒 вишня черешня",
    "🍑 персик",
    "🥭 манго",
    "🍍 ананас",
    "🥥 кокос",
    "🥝 киви",
    "🍅 помидор",
    "🥑 авокадо",
    "🥦 брокколи",
    "🥕 морковь",
    "🌽 кукуруза",
    "🌶️ перец острый",
    "🥔 картошка",
    "🍞 хлеб",
    "🧀 сыр",
    "🥚 яйцо",
    "🍳 яичница завтрак",
    "🥞 блины панкейки",
    "🥓 бекон",
    "🍔 бургер",
    "🍟 картошка фри",
    "🍕 пицца",
    "🌭 хот-дог",
    "🌮 тако",
    "🍜 лапша рамен",
    "🍣 суши",
    "🍦 мороженое",
    "🍩 пончик",
    "🍪 печенье",
    "🎂 торт день рождения",
    "🍫 шоколад",
    "🍿 попкорн",
    "☕ кофе",
    "🍵 чай",
    "🍺 пиво",
    "🍷 вино",
    "🥂 шампанское тост",
    "🥤 напиток",
  ],
  Занятия: [
    "⚽ футбол мяч",
    "🏀 баскетбол",
    "🏈 американский футбол",
    "🎾 теннис",
    "🏐 волейбол",
    "🏓 пинг-понг",
    "🏸 бадминтон",
    "🥊 бокс",
    "⛳ гольф",
    "🎯 дартс цель",
    "🎳 боулинг",
    "🎮 игры геймпад",
    "🕹️ джойстик",
    "🎲 кубик игра",
    "♟️ шахматы",
    "🎸 гитара",
    "🎹 пианино",
    "🎤 микрофон караоке",
    "🎧 наушники музыка",
    "🎬 кино хлопушка",
    "🎨 рисование палитра",
    "📚 книги учёба",
    "🏃 бегу спорт",
    "🚴 велосипед",
    "🏊 плаваю",
    "🧘 медитация йога",
  ],
  Места: [
    "🚗 машина авто",
    "🚕 такси",
    "🚌 автобус",
    "🚆 поезд",
    "✈️ самолёт",
    "🚀 ракета запуск",
    "🛸 нло",
    "🚲 велосипед",
    "🛵 скутер",
    "⛵ парусник",
    "🏠 дом",
    "🏢 офис здание",
    "🏥 больница",
    "🏫 школа",
    "🗺️ карта",
    "🗿 моаи статуя",
    "🌍 земля планета",
    "🌋 вулкан",
    "🏔️ горы",
    "🏖️ пляж",
    "🌲 дерево лес",
    "🌵 кактус",
    "🌷 тюльпан цветок",
    "🌹 роза",
    "🌻 подсолнух",
    "🍀 клевер удача",
    "🌈 радуга",
    "☀️ солнце ясно",
    "🌤️ облачно солнце",
    "☁️ облако",
    "🌧️ дождь",
    "⛈️ гроза",
    "🌨️ снег",
    "🌙 луна ночь",
  ],
  Работа: [
    "💻 ноутбук работа",
    "🖥️ компьютер монитор",
    "⌨️ клавиатура",
    "🖱️ мышь",
    "🖨️ принтер",
    "📱 телефон смартфон",
    "☎️ телефон звонок",
    "📞 трубка звонок",
    "📷 фото камера",
    "🎥 видео камера",
    "🔋 батарея заряд",
    "🔌 розетка питание",
    "💡 лампочка идея",
    "🔦 фонарик",
    "🔧 гаечный ключ починить",
    "🔨 молоток",
    "🪛 отвёртка",
    "⚙️ шестерёнка настройки",
    "🧰 инструменты",
    "🧲 магнит",
    "🧪 пробирка тест",
    "🔬 микроскоп",
    "🛠️ инструменты правка",
    "📦 коробка посылка",
    "📎 скрепка вложение",
    "📌 кнопка закрепить",
    "📝 заметка пишу",
    "📄 документ файл",
    "📊 график диаграмма",
    "📈 рост график",
    "📉 падение график",
    "🗑️ корзина удалить",
    "🔍 поиск лупа",
    "🔑 ключ доступ",
    "⏰ будильник время",
    "⌛ песочные часы ждать",
    "📅 календарь дата",
    "✉️ письмо почта",
  ],
}

export const EMOJI_GROUPS: EmojiGroup[] = Object.entries(CATALOGUE).map(
  ([name, entries]) => ({
    name,
    emoji: entries.map((entry) => {
      const space = entry.indexOf(" ")
      return {
        char: entry.slice(0, space),
        keywords: entry.slice(space + 1),
      }
    }),
  })
)

const BY_CHAR = new Map(
  EMOJI_GROUPS.flatMap((group) => group.emoji).map((e) => [e.char, e])
)

/** Everything whose keywords contain `query`, in catalogue order. */
export function searchEmoji(query: string): Emoji[] {
  const needle = query.trim().toLowerCase()
  if (!needle) return []

  // Word starts, not substrings: "ко" should find "кот", not every word
  // with those letters somewhere in the middle.
  return EMOJI_GROUPS.flatMap((group) => group.emoji).filter((emoji) =>
    emoji.keywords.split(" ").some((word) => word.startsWith(needle))
  )
}

// --- Recently used ---------------------------------------------------------

const RECENT_KEY = "chatforge:recentEmoji"
const RECENT_LIMIT = 16

/**
 * The emoji this device sent last, newest first. Stored rather than derived
 * because the messages a person sent are not all in memory, and a picker that
 * forgets between reloads is a picker people stop using.
 */
export function loadRecentEmoji(): Emoji[] {
  let raw: string | null = null
  try {
    raw = localStorage.getItem(RECENT_KEY)
  } catch {
    // Private browsing, or site data blocked: no recents, no error.
  }
  if (!raw) return []

  try {
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []

    // Anything no longer in the catalogue is dropped, so a trimmed list never
    // leaves an unlabelled character behind.
    return parsed
      .filter((char): char is string => typeof char === "string")
      .map((char) => BY_CHAR.get(char))
      .filter((emoji): emoji is Emoji => emoji !== undefined)
      .slice(0, RECENT_LIMIT)
  } catch {
    return []
  }
}

/** Moves `char` to the front of the recents and returns the new list. */
export function rememberEmoji(char: string): Emoji[] {
  const picked = BY_CHAR.get(char)
  if (!picked) return loadRecentEmoji()

  const next = [
    picked,
    ...loadRecentEmoji().filter((emoji) => emoji.char !== char),
  ].slice(0, RECENT_LIMIT)

  try {
    localStorage.setItem(RECENT_KEY, JSON.stringify(next.map((e) => e.char)))
  } catch {
    // The list still updates for this session.
  }

  return next
}
